import {
    ApiBaseService,
    type ApiServiceConfig,
} from "~/services/ApiBaseService";
import type { ApiResponse } from "~/types/ApiResponse";
import type { FieldDefinition } from "~/models/Automation";

export interface CarerixField {
    "allows-null": string;
    "data-type": string;
    name: string;
    label?: string;
    options?: {
        value: string;
        label: string;
        key: string;
    }[];
    "destination-entity"?: string;
    "value-class": string;
}

export enum CarerixAttributeType {
    CampaignList = "campaignListType",
    DataNodeList = "dataNodeListType",
    AgencyList = "agencyListType",
    UserOnlyAgencyList = "userOnlyAgencyListType",
    UserRestrictedList = "userRestrictedListType",
    UserList = "userListType",
    Password = "passwordType",
    DateTime = "dateTimeType",
    RadiobuttonList = "radiobuttonListType",
    CheckboxList = "checkboxListType",
    List = "listType",
    Checkbox = "checkboxType",
    Date = "dateType",
    Number = "numberType",
    Integer = "integerType",
    Textarea = "textareaType",
    Url = "urlType",
    String = "stringType",
}

export interface CarerixDataNodeChild {
    _id: string;
    value: any;
}

export interface CarerixDataNode {
    _id: string;
    _kind: string;
    active: boolean;
    deleted: boolean;
    isEditable: boolean;
    isHidden: boolean;
    isDefault: boolean;
    isNotVisible: boolean;
    isStatusReadOnly: boolean;
    color: string | null;
    notes: string | null;
    notesInfoAttributeLabel: string;
    notesInfoAttributePositionTabID: number;
    notesInfoAttributePositionPanelID: number;
    notesInfoAttributeSearchable: boolean;
    notesInfoAttributeVisibleInPreviewPanel: boolean;
    notesInfoNodeType: number;
    tag: CarerixAttributeType | null;
    value: any;
    children: CarerixDataNodeChild[];
}

export interface CarerixDataNodeList {
    _id: string;
    _kind: string;
    identifier: string;
    localizedName: string;
    name: string;
    items: CarerixDataNode[];
}

export interface CarerixWebhook {
    id: string;
    url: string;
    filters: string[];
    status: "enabled" | "disabled";
    headers: Record<string, string | string[]>;
    created_at: string;
    updated_at: string;
}

export default class CarerixService extends ApiBaseService {
    protected uuid: string;

    constructor(uuid: string, config: ApiServiceConfig) {
        super(config);

        this.uuid = uuid;
    }

    public describe(
        object: string,
        options?: Partial<{
            updatable: boolean;
        }>,
    ): Promise<
        ApiResponse<{
            label: string;
            value: string;
            fields: FieldDefinition[];
        }>
    > {
        return this.request(
            this.route(
                "api.connections.carerix.describe",
                "/connections/carerix/{connection}/{object}",
                {
                    connection: this.uuid,
                    object,
                },
            ),
            {
                method: "GET",
                query: options,
            },
        );
    }

    public updatableObjects(): Promise<
        ApiResponse<
            {
                label: string;
                value: string;
            }[]
        >
    > {
        return this.request(
            this.route(
                "api.connections.carerix.updatableObjects",
                "/connections/carerix/{connection}/updatable_objects",
                {
                    connection: this.uuid,
                },
            ),
            {
                method: "GET",
            },
        );
    }

    public webhooks(): Promise<ApiResponse<CarerixWebhook[]>> {
        return this.request(
            this.route(
                "api.connections.carerix.webhooks",
                "/connections/carerix/{connection}/webhooks",
                {
                    connection: this.uuid,
                },
            ),
            {
                method: "GET",
            },
        );
    }

    public syncWebhooks(): Promise<void> {
        return this.request(
            this.route(
                "api.connections.carerix.webhooks",
                "/connections/carerix/{connection}/webhooks",
                {
                    connection: this.uuid,
                },
            ),
            {
                method: "POST",
            },
        );
    }

    public toggleWebhook(id: string): Promise<ApiResponse<CarerixWebhook>> {
        return this.request(
            this.route(
                "api.connections.carerix.toggleWebhook",
                "/connections/carerix/{connection}/webhooks/{id}",
                {
                    connection: this.uuid,
                    id,
                },
            ),
            {
                method: "POST",
            },
        );
    }

    public deleteWebhook(id: string): Promise<void> {
        return this.request(
            this.route(
                "api.connections.carerix.deleteWebhook",
                "/connections/carerix/{connection}/webhooks/{id}",
                {
                    connection: this.uuid,
                    id,
                },
            ),
            {
                method: "DELETE",
            },
        );
    }

    public getObjectWithRelations(
        object: string,
        depth?: number,
    ): Promise<
        ApiResponse<
            {
                label: string;
                value: string;
            }[]
        >
    > {
        return this.request(
            this.route(
                "api.connections.carerix.object-with-relations",
                "/connections/carerix/{connection}/object-with-relations/{object}",
                {
                    connection: this.uuid,
                    object,
                },
            ),
            {
                method: "GET",
                query: {
                    depth,
                },
            },
        );
    }

    public query(
        object: string,
        options: Partial<{
            show: number;
            fields: string;
            options: boolean;
        }>,
    ): Promise<ApiResponse<any>> {
        return this.request(
            this.route(
                "api.connections.carerix.query",
                "/connections/carerix/{connection}/query/{object}",
                {
                    connection: this.uuid,
                    object,
                },
            ),
            {
                method: "GET",
                query: options,
            },
        );
    }

    public getNodeListType(
        nodeListType: string,
    ): Promise<ApiResponse<CarerixDataNodeList>> {
        return this.request(
            this.route(
                "api.connections.carerix.query",
                "/connections/carerix/{connection}/getNodeListType/{nodeListType}",
                {
                    connection: this.uuid,
                    nodeListType,
                },
            ),
            {
                method: "GET",
            },
        );
    }
}
